// Event-driven toast singleton: call `toast(kind, message)` anywhere and mount
// <ToastViewport /> once at the app root.

import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import clsx from 'clsx';
import { useAdminUiLabels } from '../i18n';

type ToastKind = 'info' | 'success' | 'error';

interface ToastPayload {
  id: number;
  kind: ToastKind;
  message: string;
}

const TOAST_EVENT = 'bak-admin:toast';

export function toast(kind: ToastKind, message: string): void {
  const detail: ToastPayload = { id: Date.now() + Math.random(), kind, message };
  window.dispatchEvent(new CustomEvent<ToastPayload>(TOAST_EVENT, { detail }));
}

const STYLE: Record<ToastKind, { bar: string; iconClass: string; Icon: typeof Info }> = {
  info: {
    bar: 'bg-[var(--color-trust-blue)]',
    iconClass: 'text-[var(--color-trust-blue)]',
    Icon: Info,
  },
  success: {
    bar: 'bg-[var(--color-success)]',
    iconClass: 'text-[var(--color-success)]',
    Icon: CheckCircle2,
  },
  error: {
    bar: 'bg-[var(--color-danger)]',
    iconClass: 'text-[var(--color-danger)]',
    Icon: AlertCircle,
  },
};

export function ToastViewport() {
  const labels = useAdminUiLabels();
  const [items, setItems] = useState<ToastPayload[]>([]);

  useEffect(() => {
    function onToast(e: Event) {
      const detail = (e as CustomEvent<ToastPayload>).detail;
      // The same message again (e.g. "Order updated" after each step) replaces the one on screen instead of stacking.
      setItems((prev) => [...prev.filter((t) => t.kind !== detail.kind || t.message !== detail.message), detail]);
      window.setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== detail.id));
      }, 5000);
    }
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, []);

  function dismiss(id: number) {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }

  return (
    <div className="pointer-events-none fixed left-4 right-4 top-20 z-50 flex flex-col items-end gap-2 sm:left-auto">
      {items.map((t) => {
        const { bar, iconClass, Icon } = STYLE[t.kind];
        return (
          <div
            key={t.id}
            className={clsx(
              'pointer-events-auto flex w-full max-w-md items-start gap-3 overflow-hidden',
              'rounded-[var(--radius-md)] border border-[var(--mist-gray)] bg-[var(--bg-page)] shadow-[var(--shadow-md)]',
            )}
            role={t.kind === 'error' ? 'alert' : 'status'}
          >
            <div className={clsx('w-1.5 self-stretch', bar)} aria-hidden />
            <Icon className={clsx('mt-3 h-5 w-5', iconClass)} aria-hidden />
            <p className="flex-1 py-3 font-[var(--font-body)] text-sm text-[var(--fg-default)]">
              {t.message}
            </p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="self-start p-2 text-[var(--fg-quiet)] transition-colors hover:text-[var(--fg-default)]"
              aria-label={labels.close}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
